import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MoneyField } from "@/components/acquisitions/MoneyField";
import type { Fund } from "@/lib/api/acquisitions";
import type { RequestFormState } from "./request-form-state";

type RequestAcquisitionFieldsProps = {
  form: RequestFormState;
  funds: Fund[];
  onChange: (patch: Partial<RequestFormState>) => void;
};

export function RequestAcquisitionFields({ form, funds, onChange }: RequestAcquisitionFieldsProps) {
  return (
    <div className="space-y-4 rounded-lg border bg-white p-6">
      <h3 className="font-semibold text-slate-900">Acquisition details</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="request-quantity">Quantity</Label>
          <Input
            id="request-quantity"
            type="number"
            min="1"
            value={form.quantity}
            onChange={(event) => onChange({ quantity: event.target.value })}
            placeholder="1"
          />
        </div>
        <MoneyField
          id="request-cost"
          label="Estimated unit cost"
          value={form.estimatedCost}
          onChange={(estimatedCost) => onChange({ estimatedCost })}
        />
        <div className="space-y-1.5">
          <Label>Urgency</Label>
          <Select value={form.urgency} onValueChange={(urgency) => onChange({ urgency })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="LOW">Low</SelectItem>
              <SelectItem value="MEDIUM">Medium</SelectItem>
              <SelectItem value="HIGH">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Fund</Label>
        <Select
          value={form.fundId || "__none__"}
          onValueChange={(fundId) => onChange({ fundId: fundId === "__none__" ? "" : fundId })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a fund (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No fund</SelectItem>
            {funds.map((fund) => (
              <SelectItem key={fund.id} value={fund.id}>
                {fund.name} ({fund.code}) — Available: {fund.available.toLocaleString()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="request-justification">Justification</Label>
        <Textarea
          id="request-justification"
          value={form.justification}
          onChange={(event) => onChange({ justification: event.target.value })}
          rows={3}
          placeholder="Why is this acquisition needed?"
        />
      </div>
    </div>
  );
}
